import { useEffect, useMemo, useState } from "react";
import {
  Building2,
  Landmark,
  RadioTower,
  RotateCcw,
  Search,
} from "lucide-react";
import useSWR from "swr";
import type { VideoStreamCard } from "./VideoCard";
import { getSearchBenches, getSearchHighCourts } from "../../api/searchApis";

type CourtType = "All" | "High Court" | "District Court";
type FilterOption = { value: string; label: string };

export interface CourtFilterValues {
  search: string;
  courtType: CourtType;
  status: string;
  highCourt: string;
  highCourtLabel: string;
  bench: string;
  benchLabel: string;
  stateName: string;
  district: string;
  complex: string;
  establishment: string;
  courtRoom: string;
}

interface CourtFilterBarProps {
  readonly rows: VideoStreamCard[];
  readonly statusOptions?: string[];
  readonly onChange: (filtered: VideoStreamCard[]) => void;
  readonly onFiltersChange?: (filters: CourtFilterValues) => void;
}

const DEFAULT_STATUS = ["All", "Live", "Scheduled"];
const ALL_OPTION = "All";
const COURT_OPTION_PAGE_SIZE = 10;

const courtTabs: Array<{
  value: CourtType;
  label: string;
  icon: React.ReactNode;
}> = [
  { value: "All", label: "All", icon: <RadioTower size={15} /> },
  { value: "High Court", label: "High Court", icon: <Landmark size={15} /> },
  {
    value: "District Court",
    label: "District Court",
    icon: <Building2 size={15} />,
  },
];

const uniqueSorted = (values: Array<string | undefined>) =>
  [...new Set(values.filter((v): v is string => Boolean(v)))].sort((a, b) =>
    a.localeCompare(b),
  );

const stringsToOptions = (values: string[]): FilterOption[] =>
  values.map((value) => ({ value, label: value }));

const normalizeText = (...values: unknown[]) =>
  values
    .find((value) => typeof value === "string" && value.trim())
    ?.toString()
    .trim() || "";

const uniqueOptions = (options: FilterOption[]) => {
  const seen = new Set<string>();

  return options
    .filter((option) => {
      const key = option.value || option.label;
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => a.label.localeCompare(b.label));
};

const getSelectedLabel = (options: FilterOption[], value: string) =>
  value === ALL_OPTION
    ? ALL_OPTION
    : options.find((option) => option.value === value)?.label || value;

const optionValues = (options: FilterOption[]) =>
  options.map((option) => option.value);

const fetchAllSearchPages = async <T,>(
  apiFn: (
    payload: any,
    pagination?: { page?: number; limit?: number },
  ) => Promise<any>,
  payload: any,
) => {
  const items: T[] = [];
  let page = 1;
  let totalPages = 1;

  do {
    const response = await apiFn(payload, {
      page,
      limit: COURT_OPTION_PAGE_SIZE,
    });

    if (!response?.status) break;

    items.push(...((response.data || []) as T[]));
    totalPages = Number(response.pagination?.totalPages) || 1;
    page += 1;
  } while (page <= totalPages);

  return items;
};

export default function CourtFilterBar({
  rows,
  statusOptions = DEFAULT_STATUS,
  onChange,
  onFiltersChange,
}: CourtFilterBarProps) {
  const [search, setSearch] = useState("");
  const [courtType, setCourtType] = useState<CourtType>("All");
  const [status, setStatus] = useState("All");
  const [highCourt, setHighCourt] = useState("All");
  const [bench, setBench] = useState("All");
  const [stateName, setStateName] = useState("All");
  const [district, setDistrict] = useState("All");
  const [complex, setComplex] = useState("All");
  const [establishment, setEstablishment] = useState("All");
  const [courtRoom, setCourtRoom] = useState("All");

  const { data: highCourtRows = [] } = useSWR(
    courtType === "High Court" ? "court-filter:search-highcourts" : null,
    () => fetchAllSearchPages<any>(getSearchHighCourts, {}),
  );

  const scopedRows = useMemo(
    () => rows.filter((r) => courtType === "All" || r.courtType === courtType),
    [rows, courtType],
  );

  const highCourtOptions = useMemo(
    () =>
      uniqueOptions(
        highCourtRows.map((court: any) => ({
          value: String(court._id || court.id || ""),
          label: normalizeText(
            court.highCourtName,
            court.high_court,
            court.name,
            court.courtName,
          ),
        })),
      ),
    [highCourtRows],
  );
  const selectedHighCourtLabel = getSelectedLabel(highCourtOptions, highCourt);

  const { data: benchRows = [] } = useSWR(
    courtType === "High Court" && highCourt !== ALL_OPTION
      ? `court-filter:search-benches:${highCourt}`
      : null,
    () => fetchAllSearchPages<any>(getSearchBenches, { highcourt: highCourt }),
  );

  const benchOptions = useMemo(
    () =>
      uniqueOptions(
        benchRows.map((benchRow: any) => ({
          value: String(benchRow._id || benchRow.id || ""),
          label: normalizeText(benchRow.benchName, benchRow.name),
        })),
      ),
    [benchRows],
  );
  const selectedBenchLabel = getSelectedLabel(benchOptions, bench);

  const highCourtOptionValues = useMemo(
    () => optionValues(highCourtOptions),
    [highCourtOptions],
  );
  const benchOptionValues = useMemo(
    () => optionValues(benchOptions),
    [benchOptions],
  );
  const stateOptions = useMemo(
    () => stringsToOptions(uniqueSorted(scopedRows.map((r) => r.state))),
    [scopedRows],
  );
  const districtOptions = useMemo(
    () =>
      stringsToOptions(
        uniqueSorted(
          scopedRows
            .filter((r) => stateName === ALL_OPTION || r.state === stateName)
            .map((r) => r.district),
        ),
      ),
    [scopedRows, stateName],
  );
  const complexOptions = useMemo(
    () =>
      stringsToOptions(
        uniqueSorted(
          scopedRows
            .filter((r) => district === ALL_OPTION || r.district === district)
            .map((r) => r.courtComplex),
        ),
      ),
    [scopedRows, district],
  );
  const establishmentOptions = useMemo(
    () =>
      stringsToOptions(
        uniqueSorted(
          scopedRows
            .filter((r) => complex === ALL_OPTION || r.courtComplex === complex)
            .map((r) => r.courtEstablishment),
        ),
      ),
    [scopedRows, complex],
  );
  const roomOptions = useMemo(
    () => stringsToOptions(uniqueSorted(scopedRows.map((r) => r.room))),
    [scopedRows],
  );

  const stateOptionValues = useMemo(
    () => optionValues(stateOptions),
    [stateOptions],
  );
  const districtOptionValues = useMemo(
    () => optionValues(districtOptions),
    [districtOptions],
  );
  const complexOptionValues = useMemo(
    () => optionValues(complexOptions),
    [complexOptions],
  );
  const establishmentOptionValues = useMemo(
    () => optionValues(establishmentOptions),
    [establishmentOptions],
  );
  const roomOptionValues = useMemo(
    () => optionValues(roomOptions),
    [roomOptions],
  );

  useEffect(() => {
    if (
      highCourt !== ALL_OPTION &&
      !highCourtOptionValues.includes(highCourt)
    ) {
      setHighCourt(ALL_OPTION);
    }
    if (bench !== ALL_OPTION && !benchOptionValues.includes(bench)) {
      setBench(ALL_OPTION);
    }
    if (stateName !== ALL_OPTION && !stateOptionValues.includes(stateName)) {
      setStateName(ALL_OPTION);
    }
    if (district !== ALL_OPTION && !districtOptionValues.includes(district)) {
      setDistrict(ALL_OPTION);
    }
    if (complex !== ALL_OPTION && !complexOptionValues.includes(complex)) {
      setComplex(ALL_OPTION);
    }
    if (
      establishment !== ALL_OPTION &&
      !establishmentOptionValues.includes(establishment)
    ) {
      setEstablishment(ALL_OPTION);
    }
    if (courtRoom !== ALL_OPTION && !roomOptionValues.includes(courtRoom)) {
      setCourtRoom(ALL_OPTION);
    }
  }, [
    highCourtOptionValues,
    benchOptionValues,
    stateOptionValues,
    districtOptionValues,
    complexOptionValues,
    establishmentOptionValues,
    roomOptionValues,
    highCourt,
    bench,
    stateName,
    district,
    complex,
    establishment,
    courtRoom,
  ]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();

    return rows.filter((row) => {
      if (courtType !== ALL_OPTION && row.courtType !== courtType) return false;
      if (status !== ALL_OPTION && row.status !== status) return false;
      if (
        highCourt !== ALL_OPTION &&
        (row.highCourt || row.court) !== selectedHighCourtLabel
      ) {
        return false;
      }
      if (bench !== ALL_OPTION && row.bench !== selectedBenchLabel)
        return false;
      if (stateName !== ALL_OPTION && row.state !== stateName) return false;
      if (district !== ALL_OPTION && row.district !== district) return false;
      if (complex !== ALL_OPTION && row.courtComplex !== complex) return false;
      if (
        establishment !== ALL_OPTION &&
        row.courtEstablishment !== establishment
      ) {
        return false;
      }
      if (courtRoom !== ALL_OPTION && row.room !== courtRoom) return false;

      if (query) {
        const haystack = [
          row.title,
          row.court,
          row.highCourt,
          row.caseNo,
          row.room,
          row.bench,
          row.state,
          row.district,
          row.courtComplex,
          row.courtEstablishment,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(query)) return false;
      }

      return true;
    });
  }, [
    rows,
    search,
    courtType,
    status,
    highCourt,
    bench,
    stateName,
    district,
    complex,
    establishment,
    courtRoom,
    selectedHighCourtLabel,
    selectedBenchLabel,
  ]);

  useEffect(() => {
    onChange(filtered);
    // onChange identity is caller-owned; only re-emit when the result changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered]);

  useEffect(() => {
    onFiltersChange?.({
      search,
      courtType,
      status,
      highCourt,
      highCourtLabel: selectedHighCourtLabel,
      bench,
      benchLabel: selectedBenchLabel,
      stateName,
      district,
      complex,
      establishment,
      courtRoom,
    });
  }, [
    onFiltersChange,
    search,
    courtType,
    status,
    highCourt,
    selectedHighCourtLabel,
    bench,
    selectedBenchLabel,
    stateName,
    district,
    complex,
    establishment,
    courtRoom,
  ]);

  const reset = () => {
    setSearch("");
    setCourtType(ALL_OPTION);
    setStatus(ALL_OPTION);
    setHighCourt(ALL_OPTION);
    setBench(ALL_OPTION);
    setStateName(ALL_OPTION);
    setDistrict(ALL_OPTION);
    setComplex(ALL_OPTION);
    setEstablishment(ALL_OPTION);
    setCourtRoom(ALL_OPTION);
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4 shadow-sm">
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search case, court, room..."
            aria-label="Search"
            className="h-12 w-full rounded-xl border border-slate-200 bg-white pl-11 pr-4 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
          />
        </div>
        <button
          type="button"
          onClick={reset}
          className="flex h-12 items-center gap-2 rounded-xl border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
        >
          <RotateCcw size={15} /> Reset
        </button>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-slate-100/80 px-3 py-2.5">
        <div className="flex items-center gap-1">
          {courtTabs.map((tab) => {
            const active = courtType === tab.value;
            return (
              <button
                key={tab.value}
                type="button"
                onClick={() => setCourtType(tab.value)}
                aria-pressed={active}
                className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold transition ${
                  active
                    ? "bg-white text-indigo-600 shadow-sm"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-1">
          {statusOptions.map((option) => {
            const active = status === option;
            return (
              <button
                key={option}
                type="button"
                onClick={() => setStatus(option)}
                aria-pressed={active}
                className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition ${
                  active
                    ? "bg-white text-indigo-600 shadow-sm"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {option}
              </button>
            );
          })}
        </div>
      </div>

      {courtType === "High Court" && (
        <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FilterSelect
            label="High Court"
            value={highCourt}
            options={highCourtOptions}
            onChange={(value) => {
              setHighCourt(value);
              setBench(ALL_OPTION);
            }}
          />
          <FilterSelect
            label="Bench"
            value={bench}
            options={benchOptions}
            onChange={setBench}
            disabled={highCourt === ALL_OPTION}
          />
        </div>
      )}

      {courtType === "District Court" && (
        <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <FilterSelect
            label="State"
            value={stateName}
            options={stateOptions}
            onChange={setStateName}
          />
          <FilterSelect
            label="District"
            value={district}
            options={districtOptions}
            onChange={setDistrict}
          />
          <FilterSelect
            label="Court Complex"
            value={complex}
            options={complexOptions}
            onChange={setComplex}
          />
          <FilterSelect
            label="Establishment"
            value={establishment}
            options={establishmentOptions}
            onChange={setEstablishment}
          />
          <FilterSelect
            label="Court Room"
            value={courtRoom}
            options={roomOptions}
            onChange={setCourtRoom}
          />
        </div>
      )}
    </div>
  );
}

function FilterSelect({
  label,
  value,
  options,
  onChange,
  disabled = false,
}: {
  label: string;
  value: string;
  options: FilterOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-semibold text-slate-700">
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm text-slate-800 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
      >
        <option value={ALL_OPTION}>All</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}
